import { useEffect, useState } from 'react';
import { useDataService } from '../../../../services/ApiDataService';
import useUiAlerts, { createAlertConfig, ERROR, WARNING } from '../../../../stores/AlertsStore';
import { checkPollingConfigStatus } from '../../infrastructure/services/PollingConfigService';
import { isSystemDistributed } from '../../infrastructure/services/CollectorsService';
import { getLogger } from '../../../../utilities/UseLog';

const log = getLogger('useMonitoredInfrastructureInit');

/**
 * Custom hook to initialize monitored infrastructure list data.
 * Checks polling configuration status and distributed collectors on component mount.
 *
 * This hook performs two initialization tasks:
 * 1. Validates polling configuration and displays warnings if needed
 * 2. Checks if the system has distributed collectors to determine column visibility
 *
 * @returns {{showPlcColumn: boolean}} Initialization state
 * @returns {boolean} showPlcColumn - Whether to show PLC column based on distributed collectors
 *
 * @example
 * function MonitoredInfrastructureList() {
 *   const { showPlcColumn } = useMonitoredInfrastructureInit();
 *   // Use showPlcColumn to conditionally render PLC-related columns
 * }
 */
export function useMonitoredInfrastructureInit() {
    const [showPlcColumn] = useState(true);
    const dataService = useDataService();
    const [, uiAlertActions] = useUiAlerts();

    useEffect(() => {
        const initializeListData = async () => {
            // Check polling configuration status
            try {
                const warnings = await checkPollingConfigStatus(dataService);
                warnings.forEach((warningMessage) => {
                    uiAlertActions.createAlert(createAlertConfig(WARNING, warningMessage));
                });
            } catch (error) {
                uiAlertActions.createAlert(
                    createAlertConfig(ERROR, `Failed to load polling configuration: ${error.message}`)
                );
            }

            // Check if system has distributed collectors
            try {
                const hasDistributedCollectors = await isSystemDistributed(dataService);
                if (!hasDistributedCollectors) {
                    log.debug(`No distributed collectors!`);
                }

                // Note: setShowPlcColumn is currently disabled in original implementation
                // setShowPlcColumn(hasDistributedCollectors);
            } catch (error) {
                uiAlertActions.createAlert(
                    createAlertConfig(ERROR, `Failed to check distributed collectors: ${error.message}`)
                );
            }
        };

        initializeListData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return { showPlcColumn };
}
